import type { ContentBlock, FollowUpChip } from '@beidou/agent-protocol';
import type { ChatMessage } from '../hooks/useChat';

const RESULT_BLOCK_TYPES = new Set([
  'carousel',
  'video_card',
  'comic_card',
  'novel_card',
  'gallery_card',
  'post_card',
  'microdrama_card',
]);

export function isResultBlock(block: ContentBlock): boolean {
  return RESULT_BLOCK_TYPES.has(block.type);
}

export function isRenderableBlock(block: ContentBlock): boolean {
  return block.type !== 'follow_up';
}

export function filterRenderableBlocks(content: ContentBlock[]): ContentBlock[] {
  return content.filter(isRenderableBlock);
}

/** split 布局：对话栏隐藏结果类 block */
export function filterChatColumnBlocks(content: ContentBlock[]): ContentBlock[] {
  return content.filter((b) => isRenderableBlock(b) && !isResultBlock(b));
}

export function extractResultBlocks(messages: ChatMessage[]): ContentBlock[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !Array.isArray(msg.content) || msg.streaming) continue;
    const results = msg.content.filter(isResultBlock);
    if (results.length > 0) return results;
  }
  return [];
}

export function extractFollowUpChips(messages: ChatMessage[]): FollowUpChip[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !Array.isArray(msg.content)) continue;
    for (const block of msg.content) {
      if (block.type === 'follow_up' && block.chips.length > 0) {
        return block.chips;
      }
    }
  }
  return [];
}

export const STATUS_LABELS: Record<string, (ctx: { tool?: string }) => string> = {
  thinking: () => '正在理解需求…',
  searching: ({ tool }) => (tool ? `正在检索 (${tool.split('.').pop()})…` : '正在检索片库…'),
  summarizing: () => '正在组织回复…',
  retrying: () => '结果不太满意，正在重试…',
};

export const FALLBACK_PROGRESS_LABEL = '正在理解需求…';

export function resolveStatusLabel(status: string, tool?: string, label?: string): string {
  if (label) return label;
  const fn = STATUS_LABELS[status];
  return fn ? fn({ tool }) : status;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** 自动滚底签名：排除 reasoningExpanded / feedbackRating 等纯 UI 状态 */
export function getAutoScrollSignature(messages: ChatMessage[], loading: boolean): string {
  const count = messages.length;
  const last = count > 0 ? messages[count - 1] : null;

  let contentDigest = 'none';
  if (last) {
    if (typeof last.content === 'string') {
      contentDigest = `s:${last.content.length}`;
    } else if (Array.isArray(last.content)) {
      const blocks = last.content;
      const markdownLen = blocks.reduce(
        (sum, block) => (block.type === 'markdown' ? sum + (block.text?.length ?? 0) : sum),
        0
      );
      contentDigest = `b:${blocks.length}:m:${markdownLen}`;
    }
  }

  return JSON.stringify({
    loading,
    count,
    lastId: last?.id ?? '',
    lastRole: last?.role ?? '',
    lastStreaming: Boolean(last?.streaming),
    lastMarkdownStreaming: Boolean(last?.markdownStreaming),
    lastCancelled: Boolean(last?.cancelled),
    lastProgressLabel: last?.progressLabel ?? '',
    lastProgressPhase: last?.progressPhase ?? '',
    contentDigest,
  });
}
